/**
 * HLOS-168-B.12: Router compartilhado para `window.__hlos_handleMessage__`.
 *
 * PROBLEMA: vault_bridge.ts e apple_llm_bridge.ts ambos registram o
 * mesmo global. O último registrado sobrescreve o anterior, fazendo com
 * que requests do vault sejam silenciosamente ignorados quando o bridge
 * LLM é inicializado depois (durante Pyodide init).
 *
 * SOLUÇÃO: router único despacha baseado no prefixo do id da mensagem.
 * - `hlv_*` → vault bridge
 * - `hlm_*` → LLM bridge
 *
 * Os bridges se registram via `registerVaultHandler` / `registerLLMHandler`
 * no setup (singleton, idempotente).
 */

type Handler = (raw: unknown) => void;

const vaultHandlers: Map<string, Handler> = new Map();
const llmHandlers: Map<string, Handler> = new Map();
let visionAnyHandler: ((raw: unknown) => void) | null = null;
let speechAnyHandler: ((raw: unknown) => void) | null = null;

let routerInstalled = false;

/**
 * Instala o handler global uma vez (idempotente). Deve ser chamado
 * pelos bridges no momento do init.
 */
export function installHlosRouterOnce(): void {
  if (routerInstalled) return;
  if (typeof globalThis === "undefined") return;
  routerInstalled = true;
  (globalThis as unknown as Record<string, unknown>).__hlos_handleMessage__ = (raw: unknown) => {
    if (!raw || typeof raw !== "object") return;
    const id = (raw as { id?: unknown }).id;
    if (typeof id !== "string") return;
    let handler: Handler | undefined;
    if (id.startsWith("hlv_")) {
      handler = vaultHandlers.get(id);
      if (handler) vaultHandlers.delete(id);
    } else if (id.startsWith("hlm_")) {
      handler = llmHandlers.get(id);
      if (handler) llmHandlers.delete(id);
    } else if (id.startsWith("vis_")) {
      // HLOS-323: vision — entrega para handler "any" registrado
      // (VisionBridge faz fan-out para múltiplos resolvers pendentes).
      if (visionAnyHandler) {
        try { visionAnyHandler(raw); } catch (e) { console.warn(e); }
      }
      return;
    } else if (id.startsWith("spk_")) {
      // HLOS-323: speech — entrega para handler "any" registrado
      // (AppleSpeechBridge faz fan-out para múltiplos resolvers pendentes).
      if (speechAnyHandler) {
        try { speechAnyHandler(raw); } catch (e) { console.warn(e); }
      }
      return;
    }
    if (!handler) return;
    try {
      handler(raw);
    } catch (e) {
      // swallow — nunca deixa o Swift crash por erro no handler TS.
      console.warn("[hlosRouter] handler threw:", e);
    }
  };
}

/** Registra um handler para um id de vault (hlv_*). Auto-cleanup após 1 chamada. */
export function registerVaultHandler(id: string, handler: Handler): void {
  installHlosRouterOnce();
  vaultHandlers.set(id, handler);
}

/** Registra um handler para um id de LLM (hlm_*). Auto-cleanup após 1 chamada. */
export function registerLLMHandler(id: string, handler: Handler): void {
  installHlosRouterOnce();
  llmHandlers.set(id, handler);
}

/**
 * HLOS-323: registra handler "any" para Vision (vis_*). VisionBridge
 * usa fan-out para múltiplos resolvers pendentes.
 */
export function registerVisionHandler(handler: Handler): void {
  installHlosRouterOnce();
  visionAnyHandler = handler;
}

/**
 * HLOS-323: registra handler "any" para Speech (spk_*). AppleSpeechBridge
 * usa fan-out para múltiplos resolvers pendentes.
 */
export function registerSpeechHandler(handler: Handler): void {
  installHlosRouterOnce();
  speechAnyHandler = handler;
}

/** Cleanup opcional para testes. */
export function clearHlosRouter(): void {
  vaultHandlers.clear();
  llmHandlers.clear();
}
